// État : Grand livre (lecture seule, temps réel).
// Détail chronologique des écritures, compte par compte, avec solde progressif.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { telechargerExport } from "@/lib/exportFichier";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";
import SelecteurCompte from "@/components/SelecteurCompte";
import { BookOpen, Download, Printer, Loader2, ChevronLeft, ChevronRight, X, FileText } from "lucide-react";

const fmt = (n) => (n ? Math.round(n).toLocaleString("fr-FR") : "—");
const anneeCourante = new Date().getFullYear();
const JOURNAUX = [
  { code: "tous", label: "Tous" }, { code: "VT", label: "Ventes" }, { code: "AC", label: "Achats" },
  { code: "BQ", label: "Banque" }, { code: "CA", label: "Caisse" },
  { code: "OD", label: "Opérations diverses" }, { code: "ODS", label: "Salaires" },
];

export default function GrandLivre() {
  const { company } = useAuth();
  const [exercices, setExercices] = useState([]);
  const [exercice, setExercice] = useState(String(anneeCourante));
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [journal, setJournal] = useState("tous");
  const [compte, setCompte] = useState(null);
  const [nonLettrees, setNonLettrees] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [resetSeq, setResetSeq] = useState(0);

  useEffect(() => {
    api.get("/exercices").then(({ data }) => {
      setExercices(data || []);
      if (data && data.length) setExercice(String(data[0].annee));
    }).catch(() => {});
  }, []);

  const params = useCallback(() => ({
    exercice: parseInt(exercice, 10),
    date_debut: dateDebut || undefined,
    date_fin: dateFin || undefined,
    journal: journal === "tous" ? undefined : journal,
    compte: compte?.numero || undefined,
    non_lettrees: nonLettrees || undefined,
  }), [exercice, dateDebut, dateFin, journal, compte, nonLettrees]);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get("/etats/grand-livre", { params: { ...params(), page, page_size: 20 } });
      setData(data);
    } catch {
      setData(null);
    } finally {
      setChargement(false);
    }
  }, [params, page]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { setPage(1); }, [exercice, dateDebut, dateFin, journal, compte, nonLettrees]);

  const exporter = async (format) => {
    try {
      await telechargerExport("/etats/grand-livre/export", params(), format, `grand_livre_${exercice}`);
    } catch {
      toast.error("L'export a échoué. Veuillez réessayer.");
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total_comptes / data.page_size)) : 1;

  return (
    <div className="fenua-fade-up" data-testid="page-grand-livre">
      <div className="no-print flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
            <BookOpen className="h-6 w-6 text-[#1D3D3D]" />
          </div>
          <div>
            <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">Grand livre</h1>
            <p className="text-sm text-[#1D3D3D]/70">Détail chronologique des mouvements, compte par compte.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" data-testid="export-csv" onClick={() => exporter("csv")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button variant="secondary" data-testid="export-xls" onClick={() => exporter("xls")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <Download className="h-4 w-4 mr-1" /> XLS
          </Button>
          <Button variant="secondary" data-testid="export-pdf" onClick={() => exporter("pdf")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <FileText className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Button data-testid="bouton-imprimer" onClick={() => window.print()}
            className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
            <Printer className="h-4 w-4 mr-1" /> Imprimer
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <div className="no-print mt-6 bg-white border border-[#4A8A8A]/20 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Exercice</Label>
          <Select value={exercice} onValueChange={setExercice}>
            <SelectTrigger data-testid="filtre-exercice" className="bg-white border-[#4A8A8A]/30 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(exercices.length ? exercices : [{ id: "x", annee: anneeCourante }]).map((e) => (
                <SelectItem key={e.id} value={String(e.annee)}>Exercice {e.annee}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Du</Label>
          <DatePicker testId="filtre-date-debut" value={dateDebut} onChange={setDateDebut} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Au</Label>
          <DatePicker testId="filtre-date-fin" value={dateFin} onChange={setDateFin} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Journal</Label>
          <Select value={journal} onValueChange={setJournal}>
            <SelectTrigger data-testid="filtre-journal" className="bg-white border-[#4A8A8A]/30 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {JOURNAUX.map((j) => <SelectItem key={j.code} value={j.code}>{j.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 col-span-2 md:col-span-4">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Compte spécifique</Label>
          {compte ? (
            <div className="flex items-center justify-between rounded-md bg-[#E8F4F4] px-3 py-2 text-sm">
              <span className="font-mono text-[#1D3D3D]">{compte.numero} — {compte.libelle}</span>
              <button data-testid="reset-compte" onClick={() => { setCompte(null); setResetSeq((s) => s + 1); }} className="text-[#4A8A8A] hover:text-red-500">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <SelecteurCompte key={resetSeq} testId="filtre-compte" onSelect={setCompte} placeholder="Tous les comptes (rechercher pour filtrer)" />
          )}
        </div>
        <label data-testid="filtre-non-lettrees" className="col-span-2 md:col-span-4 flex items-center gap-2 text-sm text-[#1D3D3D] cursor-pointer">
          <input type="checkbox" checked={nonLettrees} onChange={(e) => setNonLettrees(e.target.checked)}
            className="h-4 w-4 accent-[#1D3D3D]" />
          Afficher uniquement les écritures non lettrées
        </label>
      </div>

      {/* Contenu imprimable */}
      <div className="mt-6 zone-impression">
        <div className="bg-white border border-[#4A8A8A]/20 rounded-t-lg px-5 py-3">
          <p className="font-bold text-[#1D3D3D]">{company?.raison_sociale}</p>
          <p className="text-xs text-[#1D3D3D]/60">Grand livre — Exercice {exercice}</p>
        </div>

        {chargement ? (
          <div className="bg-white border-x border-b border-[#4A8A8A]/20 rounded-b-lg p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4A8A8A]" /></div>
        ) : !data || data.comptes.length === 0 ? (
          <p className="bg-white border-x border-b border-[#4A8A8A]/20 rounded-b-lg p-10 text-center text-sm text-[#1D3D3D]/60">Aucun mouvement pour ces critères.</p>
        ) : (
          <div className="space-y-4" data-testid="grand-livre-comptes">
            {data.comptes.map((c) => (
              <div key={c.numero} data-testid={`gl-compte-${c.numero}`} className="bg-white border border-[#4A8A8A]/20 rounded-lg overflow-hidden">
                <div className="bg-[#2D5A5A] text-white px-4 py-2 font-semibold text-sm">
                  <span className="font-mono">{c.numero}</span> — {c.libelle}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#E8F4F4] text-[#1D3D3D] text-left">
                      <th className="px-4 py-2 font-semibold">Date</th>
                      <th className="px-4 py-2 font-semibold">Libellé</th>
                      <th className="px-4 py-2 font-semibold">Référence</th>
                      <th className="px-4 py-2 font-semibold text-right">Débit</th>
                      <th className="px-4 py-2 font-semibold text-right">Crédit</th>
                      <th className="px-4 py-2 font-semibold text-right">Solde</th>
                      <th className="px-4 py-2 font-semibold text-center">Lettrage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#4A8A8A]/10">
                    {c.lignes.map((l, i) => (
                      <tr key={i} className="hover:bg-[#E8F4F4]/40">
                        <td className="px-4 py-2 text-[#1D3D3D]/80 whitespace-nowrap">{l.date}</td>
                        <td className="px-4 py-2 text-[#1D3D3D]">{l.libelle}</td>
                        <td className="px-4 py-2 font-mono text-[#4A8A8A]">{l.reference || "—"}</td>
                        <td className="px-4 py-2 text-right">{fmt(l.debit)}</td>
                        <td className="px-4 py-2 text-right">{fmt(l.credit)}</td>
                        <td className="px-4 py-2 text-right font-medium">{fmt(l.solde)}</td>
                        <td className="px-4 py-2 text-center">
                          {l.lettrage ? (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-[#4A8A8A]/15 text-[#1D3D3D] font-mono text-xs font-semibold">{l.lettrage}</span>
                          ) : <span className="text-[#1D3D3D]/30">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#E8F4F4] font-bold text-[#1D3D3D]">
                      <td className="px-4 py-2" colSpan={3}>Sous-total</td>
                      <td className="px-4 py-2 text-right">{fmt(c.total_debit)}</td>
                      <td className="px-4 py-2 text-right">{fmt(c.total_credit)}</td>
                      <td className="px-4 py-2 text-right">{fmt(c.solde_final)}</td>
                      <td className="px-4 py-2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>

      {data && totalPages > 1 && (
        <div className="no-print mt-4 flex items-center justify-center gap-3">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-precedente">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-[#1D3D3D]">Page {page} / {totalPages} · {data.total_comptes} comptes</span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="page-suivante">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
